import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

import {
  find,
  groupBy,
  map,
  mapValues,
  toString,
  isEmpty,
  has,
} from 'lodash';

import { alpha } from '@mui/material';
import { renderToString } from 'react-dom/server';
import { useTheme } from '@mui/material/styles';

import { Timescale } from '../../../../../types/models';
import {
  formatParameters,
  IFormatOutputParameters,
} from '../../../../Visualization/formatParameters';
import { IChartData, IChartDataSeries } from '../index';

import { 
  createDashPattern, 
  createPreviewPattern,
  initializeHighcharts,
  FONT_FAMILY,
  formatNumber,
  createChartConfig,
  createYAxisConfig,
  createXAxisConfig
} from './chartHelper';

// Initialize Highcharts modules
initializeHighcharts();

interface IColumnChartProps extends HighchartsReact.Props {
  chartTitle: string;
  yAxisTitle: string;
  prefix: string;
  suffix: string;
  formatKey: number;
  truncation: number;
  decimalPlaces: number;
  data: IChartData;
  timeScaleType: Timescale;
  dateFormat: string;
  isPanelCollapsed: boolean;
  showScenarios: boolean;
  selectedChartDataType: string;
  groupedChartData: (IChartDataSeries & { color: string; dataType: string })[];
}





// Helper function to create series data
const createSeriesData = (
  groupedChartData: (IChartDataSeries & { color: string; dataType: string })[]
): any[] => {
  return map(groupedChartData, (item) => {
    if (item.dataType === 'published') {
      const dashPattern = createDashPattern(item.dashStyle || 'Solid', item.color);
      
      return {
        name: item.segment,
        data: structuredClone(item.values),
        type: 'column' as const,
        linkedTo: toString(item.id),
        ...dashPattern,
        scenario: item.scenario,
        visible: item.visible,
        dataType: item.dataType,
      };
    }
    
    // Preview data with diagonal pattern
    return {
      ...createPreviewPattern(item.color),
      data: structuredClone(item.values),
      name: item.segment,
      id: toString(item.id),
      type: 'column' as const,
      scenario: item.scenario,
      visible: item.visible,
      dataType: item.dataType,
    };
  });
};

// Helper function to create tooltip data structure
const createTooltipData = (
  series: any[],
  point: any,
  formatType: string,
  prefix: string,
  suffix: string,
  truncation: number,
  decimalPlaces: number
) => {
  const groupedScenario = groupBy(series, 'userOptions.scenario');
  const chartData = mapValues(groupedScenario, (itemByScenario) => {
    return map(itemByScenario, (serie) => {
      return {
        name: serie.name,
        color: serie.color,
        dataType: serie.userOptions.dataType,
        value: formatNumber(
          Number(serie.data[point.index].y),
          formatType,
          prefix,
          suffix,
          truncation,
          decimalPlaces
        ).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        pointElement: serie.data[point.index],
      };
    });
  });

  const groupedDataByName: Record<string, any[]> = {};
  for (const key in chartData) {
    const groupedData: Record<string, any> = {};
    const dataArray = chartData[key];
    for (const item of dataArray) {
      if (!groupedData[item.name]) {
        groupedData[item.name] = {
          name: item.name,
          color: item.color,
          pointElement: item.pointElement,
          preview: 0,
          published: 0,
          dataType: item.dataType,
        };
      }
      groupedData[item.name][item.dataType] = item.value;
    }
    groupedDataByName[key] = Object.values(groupedData);
  }

  return groupedDataByName;
};

// Helper function to render tooltip table
const renderTooltipTable = (
  v: any[],
  k: string,
  point: any,
  showScenarios: boolean,
  data: IChartData,
  selectedChartDataType: string
) => {
  return (
    <div key={k} style={{marginBottom: '16px', fontFamily: FONT_FAMILY}}>
      <div style={{fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', fontFamily: FONT_FAMILY}}>{k}</div>
      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #e0e0e0', fontFamily: FONT_FAMILY}}>
        <thead>
          <tr style={{backgroundColor: '#f5f5f5'}}>
            <th style={{textAlign: 'left', padding: '6px 12px', borderBottom: '1px solid #ddd', fontWeight: '600', fontFamily: FONT_FAMILY}}>Segment</th>
            {!showScenarios &&
              !isEmpty(data.PreviewData) &&
              selectedChartDataType !== 'published' && (
                <th style={{textAlign: 'right', padding: '6px 12px', borderBottom: '1px solid #ddd', fontWeight: '600', fontFamily: FONT_FAMILY}}>Recalculated Data</th>
              )}
            {selectedChartDataType !== 'preview' && (
              <th style={{textAlign: 'right', padding: '6px 12px', borderBottom: '1px solid #ddd', fontWeight: '600', fontFamily: FONT_FAMILY}}>Original Data</th>
            )}
          </tr>
        </thead>
        <tbody>
          {map(v, (serie) => {
            return (
              <tr
                key={point.index}
                style={{
                  backgroundColor:
                    (point.series.userOptions as any).scenario === k && point.series.name === serie.name
                      ? alpha(
                          has(serie.color, 'pattern') ? serie.color.pattern.color : serie.color,
                          0.6
                        )
                      : 'transparent',
                }}
              >
                <td style={{textAlign: 'left', padding: '6px 12px', borderBottom: '1px solid #eee', fontFamily: FONT_FAMILY}}>
                  {serie.name}
                </td>
                {!showScenarios &&
                  !isEmpty(data.PreviewData) &&
                  selectedChartDataType !== 'published' && (
                    <td style={{textAlign: 'right', padding: '6px 12px', borderBottom: '1px solid #eee', fontWeight: '500', fontFamily: FONT_FAMILY}}>{serie.preview}</td>
                  )}
                {selectedChartDataType !== 'preview' && (
                  <td style={{textAlign: 'right', padding: '6px 12px', borderBottom: '1px solid #eee', fontWeight: '500', fontFamily: FONT_FAMILY}}>{serie.published}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ColumnChart: FunctionComponent<IColumnChartProps> = ({
  chartTitle,
  yAxisTitle,
  prefix,
  suffix,
  formatKey,
  truncation,
  decimalPlaces,
  data,
  timeScaleType,
  dateFormat,
  isPanelCollapsed,
  showScenarios,
  selectedChartDataType,
  groupedChartData,
}) => {
  const theme = useTheme();
  const [options, setOptions] = useState<Highcharts.Options>({});
  // const [selectAllLegends, setSelectAllLegends] = useState<boolean>(true);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const formatType = useMemo(
    () =>
      (
        find(formatParameters, {
          formatKey,
        }) as IFormatOutputParameters
      ).numberType,
    [formatKey]
  );

  useEffect(() => {
    setOptions({
      chart: createChartConfig(),
      title: { text: '' },
      exporting: { enabled: false },
      subtitle: { text: '' },
      yAxis: createYAxisConfig(yAxisTitle, formatType, prefix, suffix, truncation, decimalPlaces),
      legend: { enabled: false },
      credits: { enabled: false },
      xAxis: createXAxisConfig(data, timeScaleType, dateFormat),
      plotOptions: {
        series: {
          boostThreshold: 2000,
          label: {
            enabled: false,
            connectorAllowed: false,
          },
          marker: {
            enabled: data.Dates.length === 1,
          },
        },
      },
      tooltip: {
        useHTML: true,
        followPointer: false,
        hideDelay: 2000,
        shared: false,
        enabled: true,
        stickOnContact: true,
        style: {
          zIndex: 99999,
          pointerEvents: 'auto'
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const series = this.series.chart.series as any[];
          const point = this.point;
          
          const groupedDataByName = createTooltipData(
            series,
            point,
            formatType,
            prefix,
            suffix,
            truncation,
            decimalPlaces
          );

          const mappedData = map(groupedDataByName, (v, k) => 
            renderTooltipTable(v, k, point, showScenarios, data, selectedChartDataType)
          );

          return renderToString(
            <div style={{padding: '12px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0px 8px 32px rgba(0,0,0,0.12)', fontFamily: FONT_FAMILY,
              maxHeight: '40vh', overflowY: 'auto', display: 'block', pointerEvents: 'auto', zIndex: 10000, position: 'relative'
            }}>
              <div style={{fontWeight: 'bold', marginBottom: '8px', fontFamily: FONT_FAMILY}}>{this.x}</div>
              <div style={{marginTop: '10px', fontFamily: FONT_FAMILY}}>{mappedData}</div>
            </div>
          );
        },
      },
      series: createSeriesData(groupedChartData),
    });
  }, [
    chartTitle,
    yAxisTitle,
    prefix,
    suffix,
    formatKey,
    truncation,
    decimalPlaces,
    data,
    timeScaleType,
    dateFormat,
    selectedChartDataType,
    groupedChartData,
  ]);

  const base64Rows =
    Buffer.from(JSON.stringify(data.PublishedData)).toString('base64') +
    Buffer.from(JSON.stringify(options)).toString('base64');

  useEffect(() => {
    setTimeout(() => {
      (chartRef.current as HighchartsReact.RefObject).chart.reflow();
    }, theme.transitions.duration.leavingScreen);
  }, [isPanelCollapsed]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
      key={`${base64Rows}-column`}
      immutable
    />
  );
};

export default ColumnChart;
